import dayjs from "dayjs";
import cls from "classnames";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMessage } from "@/contexts";
import { DecimalHelper } from "@/helpers";
import { TransferHelper } from "@/domains/transfer";
import { useAppDispatch, useAppSelector } from "@/stores";
import { UserRead, UserStatus } from "@/domains/user";
import {
  operatorManageTenantSelects,
  operatorManageTenantThunks,
} from "./store";
// components
import { Button, Popover, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { CreateModal } from "./CreateModal";
import { UpdateModal, UpdateModalRef } from "./UpdateModal";

export const OperatorManageTenantsPage = () => {
  const { t } = useTranslation();

  const updateModalRef = useRef<UpdateModalRef>(null);

  const dispatch = useAppDispatch();

  const loading = useAppSelector(operatorManageTenantSelects.loading);

  const queryParams = useAppSelector(operatorManageTenantSelects.queryParams);

  const tenants = useAppSelector(operatorManageTenantSelects.tenants);

  const { messageApi } = useMessage();

  useEffect(() => {
    void dispatch(operatorManageTenantThunks.list(queryParams));
  }, [dispatch, queryParams]);

  const handleCreateModalCreate = () => {
    void messageApi.success(t("systems.Modal.tenantCreated"));
    void dispatch(operatorManageTenantThunks.list(queryParams));
  };

  const handleUpdateModalUpdate = () => {
    void dispatch(operatorManageTenantThunks.list(queryParams));
  };

  const columns: ColumnsType<UserRead> = [
    {
      width: 120,
      className: "v:top",
      title: t("domains.Tenant.username"),
      dataIndex: "username",
    },
    {
      width: 255,
      className: "v:top",
      title: t("domains.Tenant.optionAddress"),
      render(_, record) {
        return (
          <a
            target="_blank"
            href={TransferHelper.getAddressLink(record.option.address)}
          >
            {record.option.address}
          </a>
        );
      },
    },
    {
      width: 100,
      className: "v:top",
      dataIndex: ["option"],
      title: t("domains.Tenant.optionCallback"),
      render(_, record) {
        const callback = record.option.callback;
        const hasCallBack = callback !== null;

        if (hasCallBack) {
          return (
            <>
              <span className="fg:green-56">{t("systems.Status.set")}</span>
              <Popover
                content={callback}
                title={t("domains.Tenant.optionCallback")}
              >
                <Button type="primary">{t("systems.Action.show")}</Button>
              </Popover>
            </>
          );
        } else {
          return (
            <span className="fg:gray-56">{t("systems.Status.unset")}</span>
          );
        }
      },
    },
    {
      width: 100,
      className: "v:top",
      dataIndex: ["option"],
      title: t("domains.Tenant.memberDepositCallback"),
      render(_, record) {
        const callback = record.option.member_deposit_callback;
        const hasCallBack = callback !== null;

        if (hasCallBack) {
          return (
            <>
              <span className="fg:green-56">{t("systems.Status.set")}</span>
              <Popover
                content={callback}
                title={t("domains.Tenant.memberDepositCallback")}
              >
                <Button type="primary">{t("systems.Action.show")}</Button>
              </Popover>
            </>
          );
        } else {
          return (
            <span className="fg:gray-56">{t("systems.Status.unset")}</span>
          );
        }
      },
    },
    {
      width: 100,
      className: "v:top",
      dataIndex: ["option"],
      title: t("domains.Tenant.memberWithdrawCallback"),
      render(_, record) {
        const callback = record.option.member_withdraw_callback;
        const hasCallBack = callback !== null;

        if (hasCallBack) {
          return (
            <>
              <span className="fg:green-56">{t("systems.Status.set")}</span>
              <Popover
                content={callback}
                title={t("domains.Tenant.memberWithdrawCallback")}
              >
                <Button type="primary">{t("systems.Action.show")}</Button>
              </Popover>
            </>
          );
        } else {
          return (
            <span className="fg:gray-56">{t("systems.Status.unset")}</span>
          );
        }
      },
    },
    {
      width: 140,
      className: "v:top",
      title: t("domains.Tenant.unwithdrawn"),
      dataIndex: "balances",
      sorter: () => 1,
      render(_, { balances }) {
        return (
          <>
            {balances.map((balance, idx) => {
              return (
                <div key={idx}>
                  <span className="inline-block w:80 t:right">
                    {DecimalHelper.fixed(balance.amount, 2)}
                  </span>
                  <span className="inline-block w:35 t:right">
                    {balance.currency}
                  </span>
                </div>
              );
            })}
          </>
        );
      },
    },
    {
      width: 180,
      className: "v:top",
      title: t("domains.Tenant.withdrawn"),
      dataIndex: "withdrawn",
      sorter: () => 1,
      render() {
        return <div className="t:right"></div>;
      },
    },
    {
      width: 150,
      className: "v:top",
      title: t("domains.Tenant.receivedFee"),
      dataIndex: "fee",
      sorter: () => 1,
      render() {
        return <div className="t:right"></div>;
      },
    },
    {
      className: "v:top",
      title: t("domains.Tenant.note"),
      dataIndex: "note",
    },
    {
      width: 90,
      align: "right",
      className: "v:top",
      title: t("domains.Tenant.createdAt"),
      key: "created_at",
      sorter: () => 1,
      render(_, { created_at }) {
        return <div>{dayjs.$ldts(created_at)}</div>;
      },
    },
    {
      width: 210,
      title: t("domains.Tenant.status"),
      key: "status",
      render(_, record) {
        const isTransferable = record.option.address !== null;
        const TransferButton = () => (
          <Button type="primary" className="bg:gold-66">
            {t("domains.Tenant.withdraw")}
          </Button>
        );

        return (
          <>
            <div
              className={cls({
                "fg:primary": record.status === UserStatus.enabled,
                "fg:disabled": record.status === UserStatus.disabled,
              })}
            >
              {record.status === UserStatus.enabled
                ? t("systems.Status.enabled")
                : t("systems.Status.disabled")}
            </div>
            <div className="flex gap:8">
              <Button danger type="primary">
                {record.status === UserStatus.disabled
                  ? t("systems.Action.enable")
                  : t("systems.Action.disable")}
              </Button>
              <Button
                type="primary"
                onClick={() => updateModalRef.current?.open(record)}
              >
                {t("systems.Action.set")}
              </Button>
              {isTransferable && <TransferButton />}
            </div>
          </>
        );
      },
    },
  ];

  const getRowClassName = (record: UserRead) => {
    if (record.status === UserStatus.disabled) {
      return "fg:disabled";
    }
    return "";
  };

  return (
    <div className="flex flex:col gap:8">
      <div className="flex jc:end gap:8">
        <CreateModal
          onCreate={handleCreateModalCreate}
          onFail={() => messageApi.error(t("systems.Modal.tenantCreateFailed"))}
        />
      </div>

      <Table
        rowKey="id"
        tableLayout="fixed"
        loading={loading}
        columns={columns}
        dataSource={tenants}
        scroll={{ x: "1600px" }}
        rowClassName={getRowClassName}
      ></Table>

      <UpdateModal ref={updateModalRef} onUpdate={handleUpdateModalUpdate} />
    </div>
  );
};
